import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../config/api';

const initialCategory = {
  name: '',
  description: '',
  isActive: true,
};

const CategoriesAdmin = () => {
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [category, setCategory] = useState(initialCategory);
  const [search, setSearch] = useState('');

  // Fetch categories
  useEffect(() => {
    fetch(getApiUrl('/api/admin/categories'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.categories)) setCategories(data.categories);
        else if (Array.isArray(data)) setCategories(data);
        else setCategories([]);
      });
  }, []);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCategory(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle form submit (add or edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = editingCategory ? 'PUT' : 'POST';
    const url = editingCategory
      ? getApiUrl(`/api/admin/categories/${editingCategory._id}`)
      : getApiUrl('/api/admin/categories');
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'x-admin': 'true' },
      body: JSON.stringify(category),
    });
    if (res.ok) {
      alert(editingCategory ? 'Category updated!' : 'Category added!');
      setShowModal(false);
      setEditingCategory(null);
      setCategory(initialCategory);
      // Refresh category list
      fetch(getApiUrl('/api/admin/categories'), { headers: { 'x-admin': 'true' } })
        .then(res => res.json())
        .then(data => setCategories(data.categories || data));
    } else {
      alert('Failed to save category');
    }
  };

  // Handle edit
  const handleEdit = (cat) => {
    setEditingCategory(cat);
    setCategory({ ...cat });
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    const res = await fetch(getApiUrl(`/api/admin/categories/${id}`), {
      method: 'DELETE',
      headers: { 'x-admin': 'true' },
    });
    if (res.ok) {
      setCategories(categories.filter(c => c._id !== id));
    } else {
      alert('Failed to delete category');
    }
  };

  // Filter categories by search
  const filteredCategories = Array.isArray(categories)
    ? categories.filter(cat =>
        cat.name.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Category Management</h1>
        <button
          onClick={() => {
            setEditingCategory(null);
            setCategory(initialCategory);
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Category
        </button>
      </div>
      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search categories..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
          />
        </div>
      </div>
      {/* Category Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full text-sm">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left font-semibold">Name</th>
              <th className="px-4 py-2 text-left font-semibold">Description</th>
              <th className="px-4 py-2 text-center font-semibold">Active</th>
              <th className="px-4 py-2 text-center font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(filteredCategories) ? filteredCategories.map((cat) => (
              <tr key={cat._id} className="border-t">
                <td className="px-4 py-2 text-left">{cat.name}</td>
                <td className="px-4 py-2 text-left">{cat.description}</td>
                <td className="px-4 py-2 text-center">{cat.isActive ? 'Yes' : 'No'}</td>
                <td className="px-4 py-2 flex justify-center items-center space-x-2">
                  <button onClick={() => handleEdit(cat)} className="text-blue-600 hover:text-blue-800">
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button onClick={() => handleDelete(cat._id)} className="text-red-600 hover:text-red-800">
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </td>
              </tr>
            )) : null}
          </tbody>
        </table>
      </div>
      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-lg relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowModal(false)}
            >
              ×
            </button>
            <h2 className="text-xl font-semibold mb-4">{editingCategory ? 'Edit Category' : 'Add Category'}</h2>
            <form onSubmit={handleSubmit}>
              <input
                name="name"
                placeholder="Category Name"
                value={category.name}
                onChange={handleChange}
                required
                className="block mb-2 w-full border rounded px-3 py-2"
              />
              <textarea
                name="description"
                placeholder="Description"
                value={category.description}
                onChange={handleChange}
                required
                className="block mb-2 w-full border rounded px-3 py-2"
              />
              <label className="block mb-2">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={category.isActive}
                  onChange={handleChange}
                  className="mr-2"
                />
                Active
              </label>
              <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded mt-4 w-full">
                {editingCategory ? 'Update Category' : 'Save Category'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesAdmin; 